import {Route} from 'vue-router';
import store from '@/store';
import {LoginTypes} from '@/enums/LoginTypes.enum';
import {NavigationDrawerAccess} from '@/enums/_common/NavigationDrawerAccess.enum';

export const PARTNER_HOME = '/partner';

/**
 * Route meta used for access checks:
 * - `partner: true` marks views of the sales partner area. Partner logins can only open these views,
 *   other users cannot open them.
 * - `guiAccess` lists navigation keys; at least one of them must be granted to the user.
 */
export type AccessRouteMeta = {
    partner?: boolean;
    guiAccess?: NavigationDrawerAccess[];
};

type RouteMeta = AccessRouteMeta & { forAll?: boolean; guestOnly?: boolean; hideNavbar?: boolean };

/** Public pages without the app shell that partner logins may open too (legal pages, 404 and token pages without navbar) */
const PARTNER_PUBLIC_PATHS = ['/imprint', '/compliance'];

function isPublicForPartners(to: Route, meta: RouteMeta): boolean {
    if (!meta.forAll) {
        return false;
    }
    return !!meta.hideNavbar || PARTNER_PUBLIC_PATHS.includes(to.path) || to.matched.some((record) => record.path === '*');
}

/**
 * Checks if the logged in user may open a route.
 * @returns true if access is granted, otherwise the path to redirect to
 */
export function resolveRouteAccess(to: Route): true | string {
    const meta = (to.meta || {}) as RouteMeta;
    if (meta.guestOnly) {
        return true;
    }
    const isPartner = store.state.userType === LoginTypes.PARTNER;
    if (isPartner && isPublicForPartners(to, meta)) {
        return true;
    }
    if (!isPartner && meta.forAll && !meta.partner) {
        return true;
    }
    // Partners only open the partner area (forAll pages with the app shell, e.g. /substitution-inquiries, are not for them)
    if (isPartner && !meta.partner) {
        return to.path === PARTNER_HOME ? true : PARTNER_HOME;
    }
    // Employees may open the partner area read-only ("view as agency") if the menu item and read access to the partners are granted
    if (!isPartner && meta.partner && (!(meta.guiAccess && meta.guiAccess.length) || !NavigationDrawerAccess.canViewAsAgency())) {
        return to.path === '/' ? true : '/';
    }
    if (meta.guiAccess && meta.guiAccess.length) {
        const granted = NavigationDrawerAccess.getEffectiveAccess((store.state.guiAccess || []) as NavigationDrawerAccess[]);
        if (!meta.guiAccess.some((key) => granted.includes(key))) {
            const fallback = isPartner ? PARTNER_HOME : '/';
            return to.path === fallback ? true : fallback;
        }
    }
    return true;
}
